<template>
    <div id="event-list-item" class="card-v2 tw-p-0 tw-pb-6 md:tw-py-5 md:tw-pb-5">
        <div class="tw-flex tw-flex-col md:tw-flex-row">
            <!-- left column (or top on mobile) -->
            <div class="tw-mb-4 tw-border-b tw-border-gray-100 tw-text-gray-500 tw-text-sm md:tw-mb-0 md:tw-border-b-0 md:tw-border-r md:tw-text-base md:tw-text-dark">
                <router-link :to="{name: 'events.show', params: { event_slug: tEvent.slug }}" class="event-tile-left-column tw-flex tw-justify-between tw-p-6 md:tw-flex-col md:tw-items-center md:tw-p-0">
                    <div class="tw-flex tw-flex-col">
                            <span v-if="displayDate">{{ startDate }}</span>
                            <span class="md:tw-text-sm md:tw-text-gray-500" :class="{'md:tw-my-2': displayDate}">
                                <template v-if="tEvent.all_day">
                                    {{ $t('events.all_day') }}
                                </template>
                                <template v-else>
                                    {{ formattedTime.startTime }}
                                    <template v-if="formattedTime.startTime !== formattedTime.endTime">
                                        - {{ formattedTime.endTime }}
                                    </template>
                                </template>
                            </span>
                            <template v-if="tEvent.status === 'private' || tEvent.status === 'secret'">
                                <i class="far mimic-title event-status light-text tw-pt-px tw-rounded-full tw-bg-gray-0 tw-hidden md:tw-flex md:tw-items-center md:tw-justify-center" aria-hidden="true" :class="[{'far fa-lock' : tEvent.status === 'private'}, {'fas fa-eye-slash tw-mt-px': tEvent.status === 'secret'}]">
                                    <span v-if="tEvent.status === 'private'" v-text="`${$t('fields.private')} ${$t('events.singular').toLowerCase()}`"></span>
                                    <span v-if="tEvent.status === 'secret'" v-text="`${$t('events.secret')}`"></span>
                                </i>
                            </template>
                    </div>
                    <div class="user-icon-previews event-members tw-ml-2 tw--mt-px tw--pt-px md:tw-hidden">
                        <template v-for="member in members">
                            <img :key="member.id" :src="member.user.avatar" onerror="this.src='/img/avatar.png'">
                        </template>
                        <span class="tw-text-sm tw-font-semibold tw-ml-1">
                            {{ tEvent.members_count }}
                        </span>
                    </div>
                </router-link>
            </div>
            <!-- card content -->
            <div class="tw-flex tw-flex-col tw-px-6 tw-flex-1 md:tw-pr-5 md:tw-pl-6 md:tw-ml-px md:tw--mt-1">
                <router-link :to="{name: 'events.show', params: { event_slug: tEvent.slug }}" class="tw-flex tw-items-center md:tw-justify-between">
                    <div class="tw-flex tw-flex-1 tw-items-center">
                        <h2 class="tw-relative tw-font-semibold tw-mr-2 tw-leading-relaxed hover:tw-underline">
                            {{ tEvent.title }}
                            <span class="notification-count hidden notification-badge" :data-notify-id="'events.boards.' + tEvent.slug"></span>
                        </h2>
                        <div v-if="isNow" class="tw-flex tw-items-center tw-text-xs tw-text-red-500 tw-font-semibold">
                            <div class="live-dot tw-rounded-full tw-mr-1" style="width: 6px; height: 6px;"></div>
                            {{ $t('events.singular') }} {{ $t('events.is_now') }}
                        </div>
                    </div>

                    <div class="user-icon-previews event-members tw-ml-2 tw--mt-px tw--pt-px tw-hidden md:tw-flex">
                        <template v-for="member in members">
                            <img :key="member.id" :src="member.user.avatar" onerror="this.src='/img/avatar.png'">
                        </template>
                        <span class="tw-text-sm tw-font-semibold tw-ml-1">
                            {{ tEvent.members_count }}
                        </span>
                    </div>
                </router-link>

                <p class="event-description tw-mt-1 tw-text-gray-400 tw-leading-relaxed tw-text-sm md:tw-text-dark" v-html="tEvent.description_clean" v-line-clamp:30="2"></p>

                <div class="tw-flex tw-items-center tw-text-gray-400 tw-mt-2 tw-text-sm" v-if="isMember">
                    <i class="far fa-calendar-alt tw-pr-2 tw-pt-px tw-mt-px"></i>
                    {{ $t('events.you_are_signed_up') }}
                </div>

                <div class="tw-flex tw-items-center tw-text-gray-400 tw-mt-2 tw-text-sm" v-if="tEvent.past">
                    <i class="far fa-history tw-pr-2 tw-pt-px tw-mt-px"></i>
                    {{ $t('events.singular') }} {{ $t('events.has_ended').toLowerCase() }}
                </div>

                <div class="tw-flex tw-mt-3">
                    <template v-if="tEvent.status === 'private' || tEvent.status === 'secret'">
                        <i class="mimic-title event-status tw-pt-px light-text tw-rounded-full tw-bg-gray-0 tw-flex tw-items-center tw-justify-center tw-mr-2 md:tw-hidden" aria-hidden="true" :class="[{'far fa-lock' : tEvent.status === 'private'}, {'fas fa-eye-slash tw-mt-px': tEvent.status === 'secret'}]">
                            <span v-if="tEvent.status === 'private'" v-text="`${$t('fields.private')} ${$t('events.singular').toLowerCase()}`"></span>
                            <span v-if="tEvent.status === 'secret'" v-text="`${$t('events.secret')}`"></span>
                        </i>
                    </template>
					<category-pill :category="category" class="tw-mr-3"></category-pill>
                    <template v-if="tEvent.online || address">
                        <div class="pill pill-location">
                            <i class="far" :class="tEvent.online ? 'fa-globe' : 'fa-map-marker-alt'"></i>
                            {{ tEvent.online ? 'Online' : address }}
                        </div>
                    </template>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import CategoryPill from '@/resources/assets/js/vue/components/events/CategoryPill';
    import EventCategory from "../../lib/models/EventCategory";

    export default {
        name: 'EventListItem',

        components: {
            CategoryPill,
        },

        props: {
            tEvent: {
                required: true,
            },

            displayDate: {
                default: true,
            },
        },

        computed: {
	        category() {
		        return EventCategory.find(this.tEvent.category_id);
	        },

            startDate() {
                return moment(this.tEvent.start_date, 'MMMM DD, YYYY HH:mm').format('DD-MM-YYYY');
            },

            formattedTime() {
                return {
                    startTime: moment(this.tEvent.start_date, 'MMMM DD, YYYY HH:mm').format('HH:mm'),
                    endTime: moment(this.tEvent.end_date, 'MMMM DD, YYYY HH:mm').format('HH:mm'),
                };
            },

            address() {
                if (!this.tEvent.online) {
                    if (this.tEvent.muncipality !== '') {
                        return this.tEvent.muncipality;
                    } else if (this.tEvent.province !== '') {
                        return this.tEvent.province;
                    }
                }
                return false;
            },

            isNow() {
                return moment().isBetween(moment(this.tEvent.start_date, 'MMMM DD, YYYY HH:mm').format('YYYY-MM-DD HH:mm'), moment(this.tEvent.end_date, 'MMMM DD, YYYY HH:mm').format('YYYY-MM-DD HH:mm'));
            },

            isMember() {
                if (this.tEvent.participation !== null &&
                    this.tEvent.permissions.includes('VIEW') &&
                    this.tEvent.participation.status === 'active' &&
                    ['member', 'moderator', 'founder'].includes(this.tEvent.participation.role)) {
                    return true;
                }

                return false;
            },

            members() {
                return this.tEvent.memberSelection.filter((member) => member.user != null && member.status === 'active');
            },
        },
    };
</script>

<style lang="scss">
    #event-list-item {
        &.card-v2 {
            @apply tw-mb-3 tw-rounded-none;

            h2 {
                @apply tw-mb-0;
            }

            .event-description {
                word-break: normal !important;

                @screen lg {
                    max-width: 85%;
                }
            }

            .event-members-count {
                width: 20px;
                height: 20px;

                .event-members-count-overlay {
                    background: rgba(0, 0, 0, 0.35);
                }
            }

            .live-dot {
                animation: live 3500ms infinite ease-in-out;
            }

            @keyframes live {
                0% {
                    @apply tw-bg-red-500;
                }
                15% {
                    @apply tw-bg-red-300;
                }
                30% {
                    @apply tw-bg-red-500;
                }
                100% {
                    @apply tw-bg-red-500;
                }
            }

            .event-tile-left-column {
                @screen md {
                    width: 120px;
                }
            }

            @screen md {
                @apply tw-rounded-lg;
            }

            .pill-location {
                height: 30px;
                padding-top: 0.45em;
            }
        }
    }
</style>
